/**
 * Search Hooks
 *
 * TanStack Query hooks for message search and media history
 */

import { useQuery, useInfiniteQuery, type UseQueryOptions } from '@tanstack/react-query';
import * as searchService from '../services/search.service';
import type { SearchFilters, SearchResponse } from '../services/search.service';

/**
 * Hook for searching users (people)
 */
export function useUserSearch(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['search', 'users', query],
    queryFn: () => searchService.searchUsers(query),
    enabled: options?.enabled !== false && query.trim().length > 0,
  });
}

/**
 * Hook for searching channels
 */
export function useChannelSearch(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['search', 'channels', query],
    queryFn: () => searchService.searchChannels(query),
    enabled: options?.enabled !== false && query.trim().length > 0,
  });
}

/**
 * Hook for general message search
 */
export function useSearch(
  filters: SearchFilters,
  options?: UseQueryOptions<SearchResponse>
) {
  return useQuery({
    queryKey: ['search', 'messages', filters],
    queryFn: () => searchService.searchMessages(filters),
    enabled: !!filters.query || !!filters.type, // Only run if query or type is provided
    ...options,
  });
}

/**
 * Hook for infinite scroll search
 * Loads pages of results as user scrolls
 */
export function useInfiniteSearch(
  filters: Omit<SearchFilters, 'offset'>,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: ['search', 'messages', 'infinite', filters],
    queryFn: ({ pageParam }) =>
      searchService.searchMessages({
        ...filters,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // If last page has results equal to limit, there might be more
      if (lastPage.pagination.hasMore) {
        return allPages.length * (filters.limit || 50);
      }
      return undefined;
    },
    enabled: options?.enabled !== false && (!!filters.query || !!filters.type),
    initialPageParam: 0,
  });
}

/**
 * Hook for media history (images/videos)
 */
export function useMediaHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: ['media-history', scope, options?.limit, options?.offset],
    queryFn: () => searchService.getMediaHistory(scope, options),
    enabled: options?.enabled !== false && (!!scope.channelId || !!scope.dmThreadId),
  });
}

/**
 * Hook for links history
 */
export function useLinksHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: ['links-history', scope, options?.limit, options?.offset],
    queryFn: () => searchService.getLinksHistory(scope, options),
    enabled: options?.enabled !== false && (!!scope.channelId || !!scope.dmThreadId),
  });
}

/**
 * Hook for files history
 */
export function useFilesHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: ['files-history', scope, options?.limit, options?.offset],
    queryFn: () => searchService.getFilesHistory(scope, options),
    enabled: options?.enabled !== false && (!!scope.channelId || !!scope.dmThreadId),
  });
}

/**
 * Combined hook for media history with tab switching
 * Returns data for all tabs but only fetches active tab
 */
export function useMediaHistoryTabs(
  scope: { channelId?: string; dmThreadId?: string },
  activeTab: 'all' | 'media' | 'links' | 'files',
  options?: { limit?: number; offset?: number }
) {
  const all = useQuery({
    queryKey: ['media-history-tabs', 'all', scope, options?.limit, options?.offset],
    queryFn: () =>
      searchService.searchMessages({
        ...scope,
        type: 'all',
        sortBy: 'date',
        limit: options?.limit || 50,
        offset: options?.offset || 0,
      }),
    enabled: activeTab === 'all' && (!!scope.channelId || !!scope.dmThreadId),
  });

  const media = useMediaHistory(scope, {
    ...options,
    enabled: activeTab === 'media',
  });

  const links = useLinksHistory(scope, {
    ...options,
    enabled: activeTab === 'links',
  });

  const files = useFilesHistory(scope, {
    ...options,
    enabled: activeTab === 'files',
  });

  // Return the active tab's data
  switch (activeTab) {
    case 'all':
      return all;
    case 'media':
      return media;
    case 'links':
      return links;
    case 'files':
      return files;
    default:
      return all;
  }
}
